'use client'

import { useEffect } from 'react'

import { AppErrorFallback } from '@/components/misc/app-error-fallback'
import {
  isStaleClientBundleError,
  reloadOnceForStaleClientBundle,
  reloadStaleClientBundle,
} from '@/lib/client/stale-client-bundle'
import { isServerComponentRenderError } from '@/lib/client/server-component-render-error'
import { reportClientCrash } from '@/lib/ops/report-client-crash'

interface ErrorPageProps {
  error: Error & { digest?: string }
  reset: () => void
}

export default function ErrorPage({ error, reset }: ErrorPageProps) {
  const isStaleBundle = isStaleClientBundleError(error)

  useEffect(() => {
    if (!isServerComponentRenderError(error)) {
      reportClientCrash(error, {
        digest: error.digest,
        source: 'app-error',
      })
    }
    reloadOnceForStaleClientBundle(error)
  }, [error])

  return (
    <AppErrorFallback
      title="Something went wrong"
      description={
        isStaleBundle
          ? 'This page was open across an update. Reload to pick up the current version.'
          : 'An unexpected error occurred. You can try again, or go back and continue from another page.'
      }
      digest={error.digest}
      onRetry={isStaleBundle ? reloadStaleClientBundle : reset}
    />
  )
}
